import Loading from "@/components/loading/Loading";
import { Button } from "@/components/ui/button";
import {
  useAuthActions,
  useAuthError,
  useAuthLoading,
} from "@/context/auth/AuthContext";
import { meUserWithOrg } from "@/context/auth/authUtils";
import {
  assertSessionCurrent,
  saveIdentitySession,
  sessionSnapshot,
  type SessionSnapshot,
} from "@/context/auth/sessionRenewal";
import { IpcRest } from "@/hooks/ipc";
import loadStore from "@/utils/generateStore";
import { ArrowLeft } from "lucide-react";
import { Dispatch, SetStateAction, useState } from "react";
import { useNavigate } from "react-router";
import VerificationInput from "./verification-input/VerificationInput";

const IDENTITY_AUTH_ENABLED =
  import.meta.env.VITE_IDENTITY_AUTH_ENABLED === "true";

export interface Business {
  id: string;
  businessId: string;
  businessName: string;
  type: "employee" | "owner" | "buyer" | "organizer";
}

export interface Data {
  assignedBusiness: Business[];
  tempToken: string;
  refreshToken?: string;
  sessionSnapshot?: SessionSnapshot;
}

interface SelectBusinessViewProps {
  data: Data | null;
  // Tiene que aceptar el mismo estado que `data`: declararlo
  // `SetStateAction<null>` no matcheaba el setter real del login.
  setBusinessesData: Dispatch<SetStateAction<Data | null>>;
  setShowSelectBusiness: (show: boolean) => void;
  userId: string | null;
  setUserId: (id: string) => void;
  businessId: string | null;
  setBusinessId: Dispatch<SetStateAction<string | null>>;
  show2fa: boolean;
  setShow2fa: (show: boolean) => void;
}

const SelectBusiness: React.FC<SelectBusinessViewProps> = ({
  data,
  setBusinessesData,
  setShowSelectBusiness,
  userId,
  setUserId,
  businessId,
  setBusinessId,
  show2fa,
  setShow2fa,
}) => {
  const { startLogin, cleanErrors, finishLogin, loginSuccess, loginError } =
    useAuthActions();
  const loading = useAuthLoading();
  const error = useAuthError();
  const navigate = useNavigate();

  const [codeState, setCodeState] = useState<string>("");

  const employeeArray: Business[] | undefined = data?.assignedBusiness.filter(
    (business) => business.type === "employee"
  );
  const ownerArray: Business[] | undefined = data?.assignedBusiness.filter(
    (business) => business.type === "owner"
  );
  const buyerArray: Business[] | undefined = data?.assignedBusiness.filter(
    (business) => business.type === "buyer"
  );
  const organizerArray: Business[] | undefined = data?.assignedBusiness.filter(
    (business) => business.type === "organizer"
  );

  const hasEmployee = employeeArray ? employeeArray.length > 0 : false;
  const hasOwner = ownerArray ? ownerArray.length > 0 : false;
  const hasBuyer = buyerArray ? buyerArray.length > 0 : false;
  const hasOrganizer = organizerArray ? organizerArray.length > 0 : false;

  const onValidationInputsChange = (value: string) => {
    setCodeState(value);
  };

  const onSubmitCode = async () => {
    startLogin();

    try {
      const res = await IpcRest.post<any>({
        url: "/api/v1/auth/verifycode",
        body: { code: +codeState, businessId, userId },
      });

      const store = await loadStore();

      await store.set("token", res.accessToken);
      await store.set("refreshToken", res.refreshToken);

      const me = await IpcRest.get<any>({ url: "/api/v1/auth/me" });

      await store.set("businessId", meUserWithOrg(me)?.businessId);

      navigate("/");

      loginSuccess(me?.user);
    } catch (error: any) {
      loginError(error?.response?.data?.statusCode);
    }
  };

  const onSelectBusiness = async (businessId: string) => {
    startLogin();
    const store = await loadStore();
    const before = data?.sessionSnapshot ?? (await sessionSnapshot(store));

    try {
      // ── IDENTITY (org-scoped), detrás del flag. `data.tempToken` = picker_token.
      if (IDENTITY_AUTH_ENABLED) {
        await assertSessionCurrent(store, before);
        const res = await IpcRest.post<any>({
          url: "/api/v1/auth/business",
          body: { business_id: businessId },
          headers: { Authorization: `Bearer ${data?.tempToken}` },
        });

        // identity envuelve la respuesta en { data, statusCode }.
        const scopedAuth = res?.data ?? res;

        // The login response owns refresh_token; select-business only mints scoped.
        const me = await IpcRest.get<any>({
          url: "/api/v1/auth/me",
          headers: { Authorization: `Bearer ${scopedAuth.token}` },
        });
        const meUser = meUserWithOrg(me);
        await saveIdentitySession(
          store,
          scopedAuth.token,
          data?.refreshToken,
          meUser,
          before
        );

        navigate("/");
        return;
      }

      // ── LEGACY (sin cambios de flujo) ──
      const res = await IpcRest.post<any>({
        url: "/api/v1/auth/business",
        body: { businessId },
        headers: { Authorization: `Bearer ${data?.tempToken}` },
      });

      // Si tiene FA en true
      if (res.has2fa) {
        setShow2fa(true);
        setUserId(res.userId);
        setBusinessId(res.businessId);
        finishLogin();
        return;
      }

      await store.set("token", res.accessToken);
      await store.set("refreshToken", res.refreshToken);

      const me = await IpcRest.get<any>({ url: "/api/v1/auth/me" });

      await store.set("businessId", meUserWithOrg(me)?.businessId);

      navigate("/");

      loginSuccess(me?.user);
    } catch (error: any) {
      if (IDENTITY_AUTH_ENABLED) {
        try {
          await assertSessionCurrent(store, before);
        } catch {
          return;
        }
        loginError(error);
        return;
      }
      await store.delete("token");

      loginError(error);
    }
  };

  return (
    <div className="flex flex-col justify-center gap-4 w-4/5">
      {!show2fa && (
        <>
          <div className="flex flex-col">
            <p className="text-slate-200 text-xl">¿Cómo deseas acceder?</p>
            <p className="text-slate-400">
              Existen múltiples opciones para acceder a tu cuenta, selecciona la
              que desees.
            </p>
          </div>

          <div className="flex flex-col gap-2.5">
            {loading ? (
              <div className="flex justify-center py-4">
                <Loading size={48} dotClassName="bg-slate-200" />
              </div>
            ) : (
              <>
                {hasEmployee && employeeArray && (
                  <div className="bg-slate-950 p-4 flex flex-col gap-2 rounded-md">
                    <div className="text-slate-500 rounded-lg shadow-2xs">
                      Como empleado de:
                    </div>
                    {employeeArray.map((business) => (
                      <div
                        key={business.id}
                        onClick={() => onSelectBusiness(business.businessId)}
                        className="border border-slate-700 text-slate-200 rounded-md p-2.5 cursor-pointer hover:bg-slate-800"
                      >
                        {business.businessName}
                      </div>
                    ))}
                  </div>
                )}

                {hasOwner && ownerArray && (
                  <div className="bg-slate-950 p-4 flex flex-col gap-2 rounded-md">
                    <div className="text-slate-500 rounded-lg shadow-2xs">
                      Como propietario de:
                    </div>
                    {ownerArray.map((business) => (
                      <div
                        key={business.id}
                        onClick={() => onSelectBusiness(business.businessId)}
                        className="border border-slate-700 text-slate-200 rounded-md p-2.5 cursor-pointer hover:bg-slate-800"
                      >
                        {business.businessName}
                      </div>
                    ))}
                  </div>
                )}

                {hasOrganizer && organizerArray && (
                  <div className="bg-slate-950 p-4 flex flex-col gap-2 rounded-md">
                    <div className="text-slate-500 rounded-lg shadow-2xs">
                      Como organizador de (no sos el dueño):
                    </div>
                    {organizerArray.map((business) => (
                      <div
                        key={business.id}
                        onClick={() => onSelectBusiness(business.businessId)}
                        className="border border-slate-700 text-slate-200 rounded-md p-2.5 cursor-pointer hover:bg-slate-800"
                      >
                        {business.businessName}
                      </div>
                    ))}
                  </div>
                )}

                {hasBuyer && buyerArray && (
                  <div className="bg-slate-950 p-4 flex flex-col gap-2 rounded-md">
                    <div className="text-slate-500 rounded-lg shadow-2xs">
                      Como cliente de:
                    </div>
                    {buyerArray.map((business) => (
                      <div
                        key={business.id}
                        onClick={() => onSelectBusiness(business.businessId)}
                        className="border border-slate-700 text-slate-200 rounded-md p-2.5 cursor-pointer hover:bg-slate-800"
                      >
                        {business.businessName}
                      </div>
                    ))}
                  </div>
                )}
                {error.generic && (
                  <div className="text-red-500 text-base">{error.generic}</div>
                )}
              </>
            )}
          </div>

          <div className="flex justify-end items-center">
            <Button
              variant="muted"
              className="text-slate-200 border-slate-500 hover:border-slate-200 hover:bg-slate-900"
              onClick={() => {
                setShowSelectBusiness(false);
                setBusinessesData(null);
                cleanErrors();
              }}
            >
              <ArrowLeft className="icon" />
              Salir
            </Button>
          </div>
        </>
      )}

      {show2fa && (
        <>
          <div className="flex flex-col">
            <p className="text-slate-200 text-xl">Introduce tu código</p>
            <p className="text-slate-400">
              Introduce el código que te hemos enviado a tu correo electrónico.
            </p>
          </div>

          <VerificationInput
            onChange={onValidationInputsChange}
            length={5}
            loading={loading}
            defaultValue={codeState}
          />

          <Button onClick={onSubmitCode}>Enviar</Button>
        </>
      )}
    </div>
  );
};

export default SelectBusiness;
